import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getAppIcon, getAppColor, formatCurrency, formatDate, getLicenseStatusColor, sortData, isLicenseExpired } from "@/lib/utils";
import { License } from "@shared/schema";
import { AssignCardDialog } from "@/components/license-card-dialog";
import { CreditCard, Search, Eye, Edit, MoreHorizontal, UserPlus, Trash, Trash2, RotateCcw, Key, AppWindow } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import ViewLicenseDialog from "./view-license-dialog";
import EditLicenseDialog from "./edit-license-dialog";
import { SortableTableHeader, SortDirection } from "@/components/ui/sortable-table-header";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import { useAuth } from "@/hooks/use-auth";

type FilterOptions = {
  status: string;
  billingCycle: string;
  costMin: string;
  costMax: string;
  seatsMin: string;
  seatsMax: string;
  expiringWithin: string;
  vendors: string[];
  employeeId: string;
  productOwnerId: string;
};

type LicenseTableProps = {
  filterStatus: string | null;
  onAddLicense: () => void;
  customFilters?: FilterOptions;
};

export default function LicenseTable({ 
  filterStatus, 
  onAddLicense, 
  customFilters 
}: LicenseTableProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLicenseId, setSelectedLicenseId] = useState<number | null>(null);
  const [licenseToDelete, setLicenseToDelete] = useState<License | null>(null);
  const [sortKey, setSortKey] = useState<string | null>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  
  // Bulk selection state
  const [selectedLicenseIds, setSelectedLicenseIds] = useState<Set<number>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { toast } = useToast();
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  
  const { data: licenses = [], isLoading: licensesLoading } = useQuery<License[]>({
    queryKey: ['/api/licenses'],
  });
  
  const { data: applications = [], isLoading: appsLoading } = useQuery<any[]>({
    queryKey: ['/api/applications'],
  });

  // Fetch all employees for product owner filter
  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ['/api/employees'],
  });

  // When employee filter is active, fetch that employee's assigned license IDs
  const { data: employeeLicenses = [] } = useQuery<License[]>({
    queryKey: ['/api/employees', customFilters?.employeeId, 'licenses'],
    queryFn: async () => {
      const res = await fetch(`/api/employees/${customFilters!.employeeId}/licenses`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!customFilters?.employeeId && customFilters.employeeId !== "",
  });
  const employeeLicenseIds = customFilters?.employeeId
    ? new Set(employeeLicenses.map((l: License) => l.id))
    : null;

  const isLoading = licensesLoading || appsLoading;

  // Normalize billing cycle strings so "annually"/"annual" match "yearly", etc.
  const normalizeBillingCycle = (s: string) => {
    const lower = s.toLowerCase();
    if (lower === 'annually' || lower === 'annual') return 'yearly';
    return lower;
  };

  // Filter licenses based on search query, status filter, and custom filters
  const filteredLicenses = licenses.filter((license: License) => {
    // Use type-safe comparison with Number() to handle any string/number mismatches
    const app = applications.find((app: any) => Number(app.id) === Number(license.applicationId));

    // Skip licenses where application is not found (data integrity issue)
    if (!app) {
      return false;
    }

    const appName = app ? app.name.toLowerCase() : '';
    const licenseName = license.name.toLowerCase();

    const matchesSearch =
      appName.includes(searchQuery.toLowerCase()) ||
      licenseName.includes(searchQuery.toLowerCase());

    // Compute expired status early so custom status filter can use it
    const isLifetimeLicense = license.billingCycle === 'Lifetime' || license.billingCycle === 'One-Time';
    const effectivelyExpired = !isLifetimeLicense && license.status !== 'discontinued' && isLicenseExpired(license.renewalDate);

    // Apply custom filters if they exist
    if (customFilters) {
      // 1. Status filter — "expired" uses computed status, not raw DB value
      if (customFilters.status !== "all") {
        if (customFilters.status === 'expired') {
          if (!effectivelyExpired) return false;
        } else if (customFilters.status === 'active') {
          if (license.status !== 'active' || effectivelyExpired) return false;
        } else if (license.status !== customFilters.status) {
          return false;
        }
      }

      // 2. Billing cycle filter — normalize "annually"/"annual" → "yearly"
      if (customFilters.billingCycle !== "all") {
        const normalizedFilterCycle = normalizeBillingCycle(customFilters.billingCycle);
        const normalizedLicenseCycle = normalizeBillingCycle(license.billingCycle);

        if (normalizedFilterCycle === "lifetime") {
          if (normalizedLicenseCycle !== "lifetime" && normalizedLicenseCycle !== "one-time") return false;
        } else if (normalizedLicenseCycle !== normalizedFilterCycle) {
          return false;
        }
      }

      // 3. Cost range filter
      if (customFilters.costMin && Number(license.cost) < Number(customFilters.costMin)) {
        return false;
      }
      if (customFilters.costMax && Number(license.cost) > Number(customFilters.costMax)) {
        return false;
      }

      // 4. Seats range filter
      if (customFilters.seatsMin && Number(license.totalSeats) < Number(customFilters.seatsMin)) {
        return false;
      }
      if (customFilters.seatsMax && Number(license.totalSeats) > Number(customFilters.seatsMax)) {
        return false;
      }

      // 5. Expiring within filter
      if (customFilters.expiringWithin !== "all") {
        const now = new Date();
        const expiryDate = new Date(license.renewalDate);

        if (customFilters.expiringWithin === "never") {
          if (!isLifetimeLicense) return false;
        } else {
          if (isLifetimeLicense) return false;
          const daysUntilExpiry = Math.floor((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          if (daysUntilExpiry > Number(customFilters.expiringWithin)) return false;
        }
      }

      // 6. Application/Vendor filter
      if (customFilters.vendors.length > 0 && !customFilters.vendors.includes(app?.name)) {
        return false;
      }

      // 7. Assigned-to employee filter
      if (employeeLicenseIds && !employeeLicenseIds.has(license.id)) {
        return false;
      }

      // 8. Product owner filter
      if (customFilters.productOwnerId && customFilters.productOwnerId !== "") {
        if (String((license as any).productOwnerId) !== customFilters.productOwnerId) {
          return false;
        }
      }
    }

    // Apply existing tab filters
    if (filterStatus === null) {
      return matchesSearch;
    } else if (filterStatus === 'expired') {
      return matchesSearch && effectivelyExpired;
    } else if (filterStatus === 'active') {
      // Active = status is active AND not past renewal date
      return matchesSearch && license.status === 'active' && !effectivelyExpired;
    } else if (filterStatus === 'expiring') {
      // Skip lifetime licenses and already-expired licenses
      if (isLifetimeLicense || effectivelyExpired) return false;
      const now = new Date();
      const expiryDate = new Date(license.renewalDate);
      const daysUntilExpiry = Math.floor((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return matchesSearch && daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
    } else if (filterStatus === 'discontinued') {
      return matchesSearch && license.status === 'discontinued';
    } else {
      return matchesSearch && license.status && license.status === filterStatus;
    }
  });

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortKey === column) {
      // Change direction if same column clicked
      setSortDirection(currentDirection => {
        if (currentDirection === "asc") return "desc";
        if (currentDirection === "desc") return null;
        return "asc";
      });
      
      // Clear sort when cycling through all states
      if (sortDirection === "desc") {
        setSortKey(null);
      }
    } else {
      // Set new sort column and default to ascending
      setSortKey(column);
      setSortDirection("asc");
    }
  };
  
  // Apply sorting
  const sortedLicenses = sortData(filteredLicenses, sortKey, sortDirection);
  
  // Calculate total pages for pagination
  const totalItems = sortedLicenses.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  
  // Reset to page 1 if the current page is out of bounds
  // Handle current page adjustment when filter changes total pages
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);
  
  // Add event listener for edit license dialog from view dialog
  useEffect(() => {
    const handleOpenEditDialog = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.licenseId) {
        setSelectedLicenseId(customEvent.detail.licenseId);
        setEditDialogOpen(true);
      }
    };
    
    window.addEventListener('openEditLicenseDialog', handleOpenEditDialog);
    
    return () => {
      window.removeEventListener('openEditLicenseDialog', handleOpenEditDialog);
    };
  }, []);
  
  // Get paginated data
  const paginatedLicenses = sortedLicenses.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  
  // Add delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest(`/api/licenses/${id}`, { method: 'DELETE' });
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      queryClient.invalidateQueries({ queryKey: ['/api/renewals'] });
      toast({
        title: "License deleted",
        description: "The license has been deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete license",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const reactivateMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest(`/api/licenses/${id}/reactivate`, { method: 'POST' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      queryClient.invalidateQueries({ queryKey: ['/api/renewals'] });
      toast({
        title: "License reactivated",
        description: "The license is active again.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to reactivate license",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (licenseIds: number[]) => {
      const res = await apiRequest('/api/licenses/bulk-delete', { 
        method: 'POST',
        body: { licenseIds }
      });
      return res;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      queryClient.invalidateQueries({ queryKey: ['/api/renewals'] });
      setSelectedLicenseIds(new Set());
      setBulkDeleteDialogOpen(false);
      toast({
        title: "Bulk delete completed",
        description: `Successfully deleted ${data.deleted.length} license(s). ${data.errors.length > 0 ? `${data.errors.length} failed.` : ''}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete licenses",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Bulk selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(paginatedLicenses.map(license => license.id));
      setSelectedLicenseIds(allIds);
    } else {
      setSelectedLicenseIds(new Set());
    }
  };

  const handleSelectLicense = (licenseId: number, checked: boolean) => {
    const newSelected = new Set(selectedLicenseIds);
    if (checked) {
      newSelected.add(licenseId);
    } else {
      newSelected.delete(licenseId);
    }
    setSelectedLicenseIds(newSelected);
  };

  const handleBulkDelete = () => {
    if (selectedLicenseIds.size === 0) return;
    bulkDeleteMutation.mutate(Array.from(selectedLicenseIds));
  };

  const isAdmin = user?.role === 'admin';
  const allSelected = paginatedLicenses.length > 0 && paginatedLicenses.every(license => selectedLicenseIds.has(license.id));
  const someSelected = paginatedLicenses.some(license => selectedLicenseIds.has(license.id)) && !allSelected;

  return (
    <div>
      <ViewLicenseDialog 
        licenseId={selectedLicenseId}
        isOpen={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedLicenseId(null);
        }}
      />
      
      <EditLicenseDialog 
        licenseId={selectedLicenseId}
        isOpen={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedLicenseId(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete License</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{licenseToDelete?.name}"? 
              This action cannot be undone and will permanently remove the license and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setDeleteDialogOpen(false);
                setLicenseToDelete(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (licenseToDelete) {
                  deleteMutation.mutate(licenseToDelete.id);
                  setDeleteDialogOpen(false);
                  setLicenseToDelete(null);
                }
              }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete License"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    
      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bulk Delete Licenses</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedLicenseIds.size} selected license(s)? 
              This action cannot be undone and will permanently remove all selected licenses and their associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isPending}
            >
              {bulkDeleteMutation.isPending ? "Deleting..." : `Delete ${selectedLicenseIds.size} License(s)`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-lg font-semibold">Licenses</CardTitle>
          <div className="flex space-x-2">
            {isAdmin && selectedLicenseIds.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteDialogOpen(true)}
                data-testid="button-bulk-delete-licenses"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete {selectedLicenseIds.size} Selected
              </Button>
            )}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-slate-400" />
              </div>
              <Input
                type="text"
                placeholder="Search licenses..."
                className="pl-10"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              {[...Array(5)].map((_, index) => (
                <Skeleton key={index} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isAdmin && (
                      <TableHead className="w-[50px]">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onCheckedChange={handleSelectAll}
                          aria-label="Select all licenses"
                          data-testid="checkbox-select-all-licenses"
                        />
                      </TableHead>
                    )}
                    <SortableTableHeader
                      column="application.name"
                      label="Application"
                      sortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      className="table-col-app"
                    />
                    <SortableTableHeader
                      column="name"
                      label="License"
                      sortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      className="table-col-license"
                    />
                    <SortableTableHeader
                      column="assignedSeats"
                      label="Seats"
                      sortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      className="table-col-seats"
                    />
                    <SortableTableHeader
                      column="cost"
                      label="Cost"
                      sortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      className="table-col-cost"
                    />
                    <SortableTableHeader
                      column="renewalDate"
                      label="Renewal Date"
                      sortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      className="table-col-date"
                    />
                    <SortableTableHeader
                      column="productOwnerName"
                      label="Product Owner"
                      sortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      column="status"
                      label="Status"
                      sortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      className="table-col-status"
                    />
                    <TableHead className="text-right table-col-actions">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedLicenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 9 : 8} className="p-0">
                        {licenses.length === 0 && applications.length === 0 ? (
                          <EmptyState
                            icon={<AppWindow className="h-10 w-10 text-slate-400" />}
                            title="Add an application first"
                            description="Licenses belong to an application, so add the SaaS tool you're licensing before adding its license details."
                            action={
                              <Button onClick={() => setLocation("/applications")} variant="default">
                                Go to Applications
                              </Button>
                            }
                          />
                        ) : (
                          <EmptyState
                            icon={<Key className="h-10 w-10 text-slate-400" />}
                            title="No licenses found"
                            description={
                              licenses.length === 0
                                ? "Add a license to start tracking cost, seats and renewal dates."
                                : "Try adjusting your search or filters."
                            }
                            action={
                              licenses.length === 0 ? (
                                <Button onClick={onAddLicense} variant="default">
                                  Add License
                                </Button>
                              ) : undefined
                            }
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedLicenses.map((license: License) => {
                      const app = applications.find((app: any) => app.id === license.applicationId);
                      const usagePercentage = Math.round((license.assignedSeats / license.totalSeats) * 100);
                      const licenseStatus = (license.status || '').toLowerCase();
                      const canReactivateLicense = ['discontinued', 'inactive', 'deactivated'].includes(licenseStatus);
                      
                      return (
                        <TableRow key={license.id}>
                          {isAdmin && (
                            <TableCell>
                              <Checkbox
                                checked={selectedLicenseIds.has(license.id)}
                                onCheckedChange={(checked) => handleSelectLicense(license.id, checked as boolean)}
                                aria-label={`Select ${license.name}`}
                                data-testid={`checkbox-license-${license.id}`}
                              />
                            </TableCell>
                          )}
                          <TableCell className="table-col-app">
                            <div className="flex items-center space-x-3">
                              <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded ${getAppColor(app?.logo || null)} flex items-center justify-center flex-shrink-0`}>
                                <i className={`${getAppIcon(app?.logo || null)} text-sm sm:text-base`}></i>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-base text-slate-900 truncate">{app?.name}</div>
                                <div className="text-xs text-slate-500 truncate">{license.name}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="table-col-license">
                            <div className="text-sm text-muted-foreground truncate">{app?.vendor}</div>
                            <div className="text-xs text-muted-foreground">
                              {license.name.toLowerCase().includes("subscription") ? "Subscription" : 
                               license.billingCycle === "Lifetime" ? "Perpetual" : "Standard"}
                            </div>
                          </TableCell>
                          <TableCell className="table-col-seats">
                            <div className="flex items-center gap-2">
                              <span className="font-medium whitespace-nowrap">{license.assignedSeats}/{license.totalSeats}</span>
                              <div className="w-12 sm:w-16 flex-shrink-0 bg-slate-200 h-2 rounded-full">
                                <div 
                                  className={`h-2 rounded-full ${usagePercentage > 90 ? 'bg-red-500' : usagePercentage > 70 ? 'bg-primary' : 'bg-amber-400'}`} 
                                  style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                                ></div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="table-col-cost">
                            <div className="font-medium whitespace-nowrap">{formatCurrency(license.cost)}</div>
                            <div className="text-sm text-muted-foreground">{license.billingCycle}</div>
                          </TableCell>
                          <TableCell className="table-col-date">
                            <span className="whitespace-nowrap">{formatDate(license.renewalDate || null)}</span>
                          </TableCell>
                          <TableCell>
                            {(license as any).productOwnerName ? (
                              <div className="flex items-center gap-1.5">
                                <div className="h-6 w-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                  {(license as any).productOwnerName.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm truncate max-w-[120px]">{(license as any).productOwnerName}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="table-col-status">
                            {(() => {
                              const isLifetime = license.billingCycle === 'Lifetime' || license.billingCycle === 'One-Time';
                              const isExpired = !isLifetime && license.status !== 'discontinued' && isLicenseExpired(license.renewalDate);
                              const displayStatus = isExpired ? 'expired' : (license.status || 'unknown');
                              const displayLabel = displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1);
                              return (
                                <Badge className={getLicenseStatusColor(displayStatus)}>
                                  {displayLabel}
                                </Badge>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setSelectedLicenseId(license.id);
                                  setViewDialogOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setSelectedLicenseId(license.id);
                                  setEditDialogOpen(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel>More Actions</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedLicenseId(license.id);
                                      setViewDialogOpen(true);
                                    }}
                                  >
                                    <UserPlus className="h-4 w-4 mr-2" />
                                    Manage Assignments
                                  </DropdownMenuItem>
                                  <DropdownMenuItem asChild>
                                    <AssignCardDialog license={license}>
                                      <div className="flex items-center px-2 py-1.5 text-sm">
                                        <CreditCard className="mr-2 h-4 w-4" />
                                        Assign Payment Card
                                      </div>
                                    </AssignCardDialog>
                                  </DropdownMenuItem>
                                  {canReactivateLicense && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={() => reactivateMutation.mutate(license.id)}
                                        disabled={reactivateMutation.isPending}
                                      >
                                        <RotateCcw className="h-4 w-4 mr-2" />
                                        {reactivateMutation.isPending ? "Reactivating..." : "Reactivate License"}
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    className="text-destructive"
                                    onClick={() => {
                                      setLicenseToDelete(license);
                                      setDeleteDialogOpen(true);
                                    }}
                                  >
                                    <Trash className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {!isLoading && sortedLicenses.length > 0 && (
                <div className="border-t px-4">
                  <DataTablePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setCurrentPage(1); // Reset to first page when changing page size
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
